'use client';
import { jsx, jsxs } from 'react/jsx-runtime';
import { Cell, ResponsiveContainer, PieChart as PieChart$1, Pie, Tooltip } from 'recharts';
import { createVarsResolver, getThemeColor, rem, factory, useProps, useMantineTheme, useStyles, useResolvedStylesApi, Box } from '@mantine/core';
import { ChartTooltip } from '../ChartTooltip/ChartTooltip.mjs';
import classes from './PieChart.module.css.mjs';

const defaultProps = {
  withTooltip: false,
  withLabelsLine: true,
  paddingAngle: 0,
  size: 160,
  strokeWidth: 1,
  startAngle: 0,
  endAngle: 360,
  tooltipDataSource: "all",
  labelsPosition: "outside",
  labelsType: "value"
};
const varsResolver = createVarsResolver(
  (theme, { strokeColor, labelColor, withLabels, size, labelsPosition }) => ({
    root: {
      "--chart-stroke-color": strokeColor ? getThemeColor(strokeColor, theme) : void 0,
      "--chart-labels-color": labelColor ? getThemeColor(labelColor, theme) : void 0,
      "--chart-size": withLabels && labelsPosition === "outside" ? rem(size + 80) : rem(size)
    }
  })
);
const getLabelValue = (labelsType, value, percent, valueFormatter) => {
  if (labelsType === "percent") {
    return `${(percent * 100).toFixed(0)}%`;
  }
  if (typeof valueFormatter === "function") {
    return valueFormatter(value);
  }
  return value;
};
const getInsideLabel = (labelsType, valueFormatter) => ({ cx, cy, midAngle, innerRadius, outerRadius, value, percent }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return /* @__PURE__ */ jsx(
    "text",
    {
      x,
      y,
      textAnchor: x > cx ? "start" : "end",
      dominantBaseline: "central",
      className: classes.label,
      children: getLabelValue(labelsType, value, percent, valueFormatter)
    }
  );
};
const getOutsideLabel = (labelsType, valueFormatter) => ({ x, y, cx, cy, percent, value }) => /* @__PURE__ */ jsx(
  "text",
  {
    x,
    y,
    cx,
    cy,
    textAnchor: x > cx ? "start" : "end",
    fill: "var(--chart-labels-color, var(--mantine-color-dimmed))",
    fontFamily: "var(--mantine-font-family)",
    fontSize: 12,
    children: /* @__PURE__ */ jsx("tspan", { x, children: getLabelValue(labelsType, value, percent, valueFormatter) })
  }
);
const PieChart = factory((_props, ref) => {
  const props = useProps("PieChart", defaultProps, _props);
  const {
    classNames,
    className,
    style,
    styles,
    unstyled,
    vars,
    data,
    withTooltip,
    tooltipAnimationDuration,
    tooltipProps,
    pieProps,
    paddingAngle,
    withLabels,
    withLabelsLine,
    size,
    strokeWidth,
    startAngle,
    endAngle,
    tooltipDataSource,
    children,
    pieChartProps,
    labelsPosition,
    valueFormatter,
    labelsType,
    strokeColor,
    ...others
  } = props;
  const theme = useMantineTheme();
  const getStyles = useStyles({
    name: "PieChart",
    classes,
    props,
    className,
    style,
    classNames,
    styles,
    unstyled,
    vars,
    varsResolver
  });
  const { resolvedClassNames, resolvedStyles } = useResolvedStylesApi({
    classNames,
    styles,
    props
  });
  const cells = data.map((item, index) => /* @__PURE__ */ jsx(
    Cell,
    {
      fill: getThemeColor(item.color, theme),
      stroke: "var(--chart-stroke-color, var(--mantine-color-body))",
      strokeWidth
    },
    index
  ));
  return /* @__PURE__ */ jsx(Box, { ref, size, ...getStyles("root"), ...others, children: /* @__PURE__ */ jsx(ResponsiveContainer, { children: /* @__PURE__ */ jsxs(PieChart$1, { ...pieChartProps, children: [
    /* @__PURE__ */ jsx(
      Pie,
      {
        data,
        innerRadius: 0,
        outerRadius: size / 2,
        dataKey: "value",
        isAnimationActive: false,
        paddingAngle,
        startAngle,
        endAngle,
        label: withLabels ? labelsPosition === "inside" ? getInsideLabel(labelsType || "value", valueFormatter) : getOutsideLabel(labelsType || "value", valueFormatter) : false,
        labelLine: withLabelsLine && labelsPosition === "outside" ? {
          stroke: "var(--chart-label-color, var(--mantine-color-dimmed))",
          strokeWidth: 1
        } : false,
        ...pieProps,
        children: cells
      }
    ),
    withTooltip && /* @__PURE__ */ jsx(
      Tooltip,
      {
        animationDuration: tooltipAnimationDuration,
        isAnimationActive: false,
        content: ({ payload }) => /* @__PURE__ */ jsx(
          ChartTooltip,
          {
            payload: data,
            classNames: resolvedClassNames,
            styles: resolvedStyles,
            type: "radial",
            segmentId: tooltipDataSource === "segment" ? payload?.[0]?.name : void 0,
            valueFormatter
          }
        ),
        ...tooltipProps
      }
    ),
    children
  ] }) }) });
});
PieChart.displayName = "@mantine/charts/PieChart";
PieChart.classes = classes;

export { PieChart };
//# sourceMappingURL=PieChart.mjs.map
